import { Box, Button, Flex, HStack, Icon, IconButton, Link, Stack, Text } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';
import { ReactNode } from 'react';
import { FiEye, FiFile, FiRotateCcw, FiTrash2 } from 'react-icons/fi';
import { formatFileSize } from '../../../../utils/functions';
import { getExemptionType } from '../../../../services/hooks/Exemptions/economicHyposufficiency';

interface ExistingAttachment {
  id: string;
  name: string;
  url: string;
  candidate_description?: string | null;
  size?: number;
}

const ExistingExemptionAttachments: React.FC<{ compact?: boolean; children?: ReactNode }> = ({ compact = false, children }) => {
  const { watch, setValue } = useFormContext();
  const attachments = (watch('existingExemptionAttachments') || []) as ExistingAttachment[];
  const removed = (watch('removedExemptionAttachmentIds') || []) as string[];
  const visible = attachments.filter((attachment) => !removed.includes(attachment.id));
  const originalTypeId = watch('originalExemptionTypeId') as string | undefined;
  const selectedType = watch('type_exemption') as string | undefined;
  const selectedTypeId = getExemptionType(selectedType)?.id || selectedType;
  const typeChanged = Boolean(originalTypeId && selectedTypeId !== originalTypeId);
  const canUndoRemoval = removed.length > 0 && !typeChanged;

  if (!attachments.length || (typeChanged && !visible.length)) return children ? <>{children}</> : null;

  const undoButton = canUndoRemoval && (
    <Button
      type="button"
      size="sm"
      px={3}
      bg="blue.400"
      color="white"
      leftIcon={<FiRotateCcw />}
      _hover={{ bg: 'blue.500' }}
      _active={{ bg: 'blue.600' }}
      onClick={() => setValue('removedExemptionAttachmentIds', [], { shouldDirty: true, shouldValidate: true })}
    >
      {removed.length === 1 ? 'Desfazer remoção' : 'Desfazer remoções'}
    </Button>
  );

  return (
    <Stack spacing={2} minW={0}>
      {visible.map((attachment) => (
        <Flex key={attachment.id} p={3} borderWidth="1px" borderColor="green.100" borderRadius="lg" bg="green.50" boxShadow="sm" gap={3} align="center" minW={0}>
          {compact && <Text fontSize="sm" color="gray.800" fontWeight="bold" minW={0} maxW="35%" isTruncated title={attachment.candidate_description || attachment.name}>{attachment.candidate_description || attachment.name}</Text>}
          <HStack spacing={1} minW={0} flex={1} borderLeft={compact ? '1px solid' : undefined} borderColor="gray.200" pl={compact ? 4 : 0}>
            {compact && <Icon as={FiFile} boxSize={4} color="blue.600" flexShrink={0} />}
            <Box minW={0}>
              <Link href={attachment.url} isExternal color="blue.600" fontSize="sm" fontWeight={compact ? 'medium' : undefined} isTruncated display="block" title={attachment.name}>{attachment.name}</Link>
              {!compact && attachment.candidate_description && <Text fontSize="xs" color="gray.600" overflowWrap="anywhere">{attachment.candidate_description}</Text>}
            </Box>
            {compact && typeof attachment.size === 'number' && <Text fontSize="xs" color="gray.500" flexShrink={0}>({formatFileSize(attachment.size)})</Text>}
          </HStack>
          <HStack spacing={1} flexShrink={0}>
            {compact && <IconButton as="a" href={attachment.url} target="_blank" rel="noopener noreferrer" aria-label={`Visualizar ${attachment.name}`} icon={<FiEye />} variant="ghost" size="sm" colorScheme="blue" />}
            {compact ? <IconButton aria-label={`Remover ${attachment.name}`} icon={<FiTrash2 />} variant="ghost" size="sm" colorScheme="red" onClick={() => setValue('removedExemptionAttachmentIds', [...removed, attachment.id], { shouldValidate: true })} /> : <Button size="sm" variant="ghost" colorScheme="red" leftIcon={<FiTrash2 />} onClick={() => setValue('removedExemptionAttachmentIds', [...removed, attachment.id], { shouldValidate: true })}>Remover</Button>}
          </HStack>
        </Flex>
      ))}
      {children}
      {visible.length > 0 && <Text fontSize="xs" color="gray.500">As remoções só serão aplicadas ao salvar.</Text>}
      {canUndoRemoval && (
        <Flex justify="center" w="full" pt={3}>
          {undoButton}
        </Flex>
      )}
    </Stack>
  );
};

export default ExistingExemptionAttachments;
